import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import { useProjects } from '../../../../contexts/ProjectsContext'
import { useServices } from '../../../../contexts/ServicesContext'

const SEVERITY_LEVELS = ['FATAL', 'ERROR', 'WARN', 'INFO', 'DEBUG', 'UNKNOWN']

function LogFilters({ filters, onChange }) {
  const { formatMessage } = useIntl()
  const { projects } = useProjects()
  const { services } = useServices()
  const [inputValue, setInputValue] = useState('')

  // Build all options with groups
  const allOptions = useMemo(() => {
    const projectOptions = projects.map((project) => ({
      type: 'project',
      id: project.id,
      label: project.name,
      group: formatMessage({ id: 'dockerLogs.filters.groups.project' }),
    }))

    const serviceOptions = services.map((service) => ({
      type: 'service',
      id: service.id,
      label: service.name,
      group: formatMessage({ id: 'dockerLogs.filters.groups.service' }),
    }))

    const severityOptions = SEVERITY_LEVELS.map((severity) => ({
      type: 'severity',
      id: severity,
      label: formatMessage({ id: `dockerLogs.severity.${severity}` }),
      group: formatMessage({ id: 'dockerLogs.filters.groups.severity' }),
    }))

    return [...projectOptions, ...serviceOptions, ...severityOptions]
  }, [projects, services, formatMessage])

  // Build lookup maps for converting IDs to options
  const optionsById = useMemo(() => {
    const map = {}
    allOptions.forEach((option) => {
      map[`${option.type}-${option.id}`] = option
    })
    return map
  }, [allOptions])

  // Convert filters to selected options for Autocomplete value
  const selectedOptions = useMemo(() => {
    const selected = []

    filters.projectIds.forEach((id) => {
      const option = optionsById[`project-${id}`]
      if (option) selected.push(option)
    })

    filters.serviceIds.forEach((id) => {
      const option = optionsById[`service-${id}`]
      if (option) selected.push(option)
    })

    filters.severities.forEach((id) => {
      const option = optionsById[`severity-${id}`]
      if (option) selected.push(option)
    })

    return selected
  }, [filters, optionsById])

  // Handle Autocomplete change
  const handleChange = (event, newValue) => {
    const projectIds = []
    const serviceIds = []
    const severities = []

    newValue.forEach((option) => {
      if (option.type === 'project') {
        projectIds.push(option.id)
      } else if (option.type === 'service') {
        serviceIds.push(option.id)
      } else if (option.type === 'severity') {
        severities.push(option.id)
      }
    })

    onChange({ projectIds, serviceIds, severities })
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Autocomplete
        multiple
        options={allOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label}
        value={selectedOptions}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        isOptionEqualToValue={(option, value) =>
          option.type === value.type && option.id === value.id
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={formatMessage({ id: 'dockerLogs.filters.placeholder' })}
            size="small"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={`${option.type}-${option.id}`}
              label={option.label}
              size="small"
            />
          ))
        }
        sx={{ minWidth: 300 }}
      />
    </Box>
  )
}

export default LogFilters
